import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Check,
  X,
  Eye,
  RefreshCw,
  Car,
  Briefcase,
  Clock,
  Mail,
  Phone,
  MapPin,
  FileText,
  Key
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function ApplicationsManagement() {
  const { api } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, driver, salesperson
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [filter, statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('app_type', filter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/admin/applications?${params.toString()}`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appId) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/applications/${appId}/approve`);
      toast.success('Application approved! Login credentials sent via email.');
      if (response.data.driver_login_code) {
        toast.info(`Driver Code: ${response.data.driver_login_code}`);
      }
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.put(`/admin/applications/${selectedApp.id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Application rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_assessment: 'bg-yellow-100 text-yellow-800',
      pending_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending_assessment: 'Awaiting Assessment',
      pending_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Applications</h1>
          <p className="text-slate-500">Review and manage driver & salesperson applications</p>
        </div>
        <Button onClick={fetchApplications} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex bg-slate-100 rounded-lg p-1">
          {['all', 'driver', 'salesperson'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === f ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {f === 'all' ? 'All' : f === 'driver' ? 'Drivers' : 'Salespeople'}
            </button>
          ))}
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="pending_assessment">Awaiting Assessment</option>
          <option value="pending_review">Under Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Applications List */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading applications...</div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No applications found</p>
            </div>
          ) : (
            applications.map((app) => (
              <div
                key={app.id}
                onClick={() => setSelectedApp(app)}
                className={`p-4 bg-white rounded-xl border cursor-pointer transition-all ${
                  selectedApp?.id === app.id
                    ? 'border-orange-500 shadow-md'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      app.application_type === 'driver' ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      {app.application_type === 'driver' ? (
                        <Car className="w-5 h-5 text-orange-600" />
                      ) : (
                        <Briefcase className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{app.full_name}</h3>
                      <p className="text-sm text-slate-500">{app.email}</p>
                    </div>
                  </div>
                  {getStatusBadge(app.status)}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {app.city}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(app.created_at).toLocaleDateString()}
                  </span>
                  {app.assessment_score !== null && (
                    <span className={`font-medium ${app.assessment_passed ? 'text-green-600' : 'text-red-600'}`}>
                      Score: {app.assessment_score}/5
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedApp ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  selectedApp.application_type === 'driver' ? 'bg-orange-100' : 'bg-blue-100'
                }`}>
                  {selectedApp.application_type === 'driver' ? (
                    <Car className="w-6 h-6 text-orange-600" />
                  ) : (
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <div>
                  <h2 className="font-bold text-lg text-slate-900">{selectedApp.full_name}</h2>
                  <p className="text-sm text-slate-500 capitalize">{selectedApp.application_type} Application</p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{selectedApp.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{selectedApp.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>{selectedApp.city}</span>
                </div>
                
                {selectedApp.application_type === 'driver' && (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <Car className="w-4 h-4 text-slate-400" />
                      <span className="capitalize">{selectedApp.vehicle_type}</span>
                    </div>
                    <div className="text-sm">
                      <span className={selectedApp.has_delivery_experience ? 'text-green-600' : 'text-slate-500'}>
                        {selectedApp.has_delivery_experience ? '✓' : '✗'} Prior delivery experience
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className={selectedApp.has_vehicle_insurance ? 'text-green-600' : 'text-slate-500'}>
                        {selectedApp.has_vehicle_insurance ? '✓' : '✗'} Vehicle insurance
                      </span>
                    </div>
                  </>
                )}

                {selectedApp.previous_experience && (
                  <div className="text-sm">
                    <span className="text-slate-500 block mb-1">Experience:</span>
                    <p className="text-slate-700">{selectedApp.previous_experience}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4 mb-6">
                <h3 className="font-semibold text-slate-800 mb-2">Assessment</h3>
                {selectedApp.assessment_score !== null ? (
                  <div className={`p-3 rounded-lg ${selectedApp.assessment_passed ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className={`font-medium ${selectedApp.assessment_passed ? 'text-green-700' : 'text-red-700'}`}>
                      {selectedApp.assessment_passed ? 'Passed' : 'Failed'} - {selectedApp.assessment_score}/5
                    </p>
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Assessment not completed</p>
                )}
              </div>

              {selectedApp.status === 'pending_review' && (
                <div className="space-y-3">
                  <Button
                    onClick={() => handleApprove(selectedApp.id)}
                    disabled={actionLoading}
                    className="w-full bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {actionLoading ? 'Processing...' : 'Approve Application'}
                  </Button>
                  <Button
                    onClick={() => setShowRejectModal(true)}
                    variant="outline"
                    className="w-full border-red-500 text-red-500 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject Application
                  </Button>
                </div>
              )}

              {selectedApp.status === 'approved' && selectedApp.application_type === 'driver' && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Driver Login Code
                  </h4>
                  <p className="text-sm text-orange-600">
                    Code was sent to the driver's email upon approval.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
              <Eye className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Select an application to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Reject Application</h3>
            <p className="text-slate-500 mb-4">
              Please provide a reason for rejecting this application. The applicant will be notified via email.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full p-3 border border-slate-200 rounded-lg mb-4 resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={actionLoading || !rejectionReason.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
